import { DomainError } from "./errors";
import { MAX_TITLE_CENTS } from "./finance";
import type { BaseState } from "./status-flow";

// ===== Faturamento agrupado (RF-FIN-03; 05 §4, §6, §10) =====

/** Mínimo de pedidos num agrupamento (um só = "Emitir NF-e" do próprio pedido). */
export const MIN_GROUPED_ORDERS = 2;
/** Teto de pedidos por agrupamento: limita o trabalho da transação (contas, efeitos e itens da nota). */
export const MAX_GROUPED_ORDERS = 50;
/** Situação de sistema concluída que não é venda própria (o faturamento é do agenciador). */
const NOT_GROUPABLE_CODES: readonly string[] = ["venda_agenciada"];

/** "Atendido": estado base concluído, menos a venda agenciada. */
export const isGroupableStatus = (baseState: BaseState, statusCode: string): boolean =>
  baseState === "done" && !NOT_GROUPABLE_CODES.includes(statusCode);

export type GroupableOrder = {
  id: string;
  number: number;
  contactId: string;
  baseState: BaseState;
  statusCode: string;
  totalCents: number;
  /** NF-e própria em envio ou emitida. */
  invoiced: boolean;
  /** Já está em outro faturamento agrupado ativo. */
  grouped: boolean;
};

/**
 * Problema da seleção ou null: 2 a 50 pedidos distintos, todos encontrados (empresa e escopo de quem chama), do
 * cliente escolhido, atendidos, com valor, sem NF-e própria e fora de outro agrupamento ativo.
 */
export function groupedSelectionProblem(
  contactId: string,
  ids: readonly string[],
  orders: readonly GroupableOrder[],
): { code: "invalid_selection" | "not_found"; message: string } | null {
  const bad = (message: string) => ({ code: "invalid_selection" as const, message });
  if (ids.length < MIN_GROUPED_ORDERS) return bad(`Selecione ao menos ${MIN_GROUPED_ORDERS} pedidos.`);
  if (ids.length > MAX_GROUPED_ORDERS) return bad(`No máximo ${MAX_GROUPED_ORDERS} pedidos por faturamento.`);
  if (new Set(ids).size !== ids.length) return bad("Pedido repetido na seleção.");
  const byId = new Map(orders.map((o) => [o.id, o]));
  for (const id of ids) {
    const o = byId.get(id);
    if (!o) return { code: "not_found", message: "Pedido de venda não encontrado" };
    const n = `O pedido nº ${o.number}`;
    if (o.contactId !== contactId) return bad(`${n} é de outro cliente.`);
    if (!isGroupableStatus(o.baseState, o.statusCode)) return bad(`${n} não está atendido.`);
    if (o.totalCents <= 0) return bad(`${n} está sem valor.`);
    if (o.invoiced) return bad(`${n} já tem NF-e própria.`);
    if (o.grouped) return bad(`${n} já está em outro faturamento agrupado.`);
  }
  return null;
}

/** Σ totais dos pedidos (BigInt), no teto de um título (as contas do grupo saem desse total). */
export function groupedTotalCents(orders: readonly { totalCents: number }[]): number {
  const total = orders.reduce((a, o) => a + BigInt(o.totalCents), 0n);
  if (total > BigInt(MAX_TITLE_CENTS))
    throw new DomainError("invalid_money", "O total dos pedidos passa do limite de um faturamento.");
  return Number(total);
}

/** O valor que todos os pedidos têm em comum (condição, vendedor, forma), ou null. */
export function groupedShared<T, V>(orders: readonly T[], pick: (o: T) => V | null): V | null {
  if (orders.length === 0) return null;
  const first = pick(orders[0]!);
  if (first === null) return null;
  return orders.every((o) => pick(o) === first) ? first : null;
}

const FREIGHT_MODES = [0, 1, 2, 3, 4, 9] as const;
export type GroupedFreightMode = (typeof FREIGHT_MODES)[number];

/**
 * Transporte da NF-e do agrupamento: modalidade e transportador dos pedidos quando todos iguais; senão 0 (por conta do
 * emitente) se há frete e 9 (sem frete) se não, sem transportador.
 */
export function groupedTransport(
  orders: readonly { freightMode: number; carrierId: string | null; freightCents: number }[],
  freightCents: number,
): { mode: GroupedFreightMode; carrierId: string | null } {
  const valid = (m: number): m is GroupedFreightMode => (FREIGHT_MODES as readonly number[]).includes(m);
  const mode = groupedShared(orders, (o) => o.freightMode);
  const carrier = orders[0]?.carrierId ?? null;
  if (mode !== null && valid(mode) && orders.every((o) => o.carrierId === carrier))
    return { mode, carrierId: mode === 9 ? null : carrier };
  return { mode: freightCents > 0 ? 0 : 9, carrierId: null };
}

const NOTE_MAX = 500;

/** Texto do infCpl: "Faturamento agrupado nº N — Pedidos de venda nº 10, 11, 12" (números em ordem, até 500). */
export function groupedInvoiceNote(number: number, orderNumbers: readonly number[]): string {
  const list = [...orderNumbers].sort((a, b) => a - b).join(", ");
  const text = `Faturamento agrupado nº ${number} — Pedidos de venda nº ${list}`;
  return text.length <= NOTE_MAX ? text : `${text.slice(0, NOTE_MAX - 1)}…`;
}
